import { Component, inject } from '@angular/core';
import { Subscription } from 'rxjs';

import { UserService } from '@aifs-shared/user/user.service';

import { ToDoPriorityItem } from '@aifs-shared/tracker/tracker.service';
import { TrackerTaskComponent } from '@aifs-shared/tracker/tracker-task.component';
import { Task, TaskStatus } from '@aifs-shared/tracker/task';
import { TrackerEventType } from '@aifs-shared/tracker/tracker-event';

import { UiModalService } from '@ui/ui-modal.service';
import { TaskDetail, TaskDetailStatus } from './task-detail';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { CfLoaderComponent } from '@ui/cf-loader/cf-loader.component';

@Component({
    templateUrl: './hub.component.html',
    styleUrls: ['./hub.component.scss'],
    imports: [CommonModule, RouterLink, CfLoaderComponent]
})

export class HubComponent extends TrackerTaskComponent {
    private userService = inject(UserService);
    private modalService = inject(UiModalService);

    constructor() {
        super();
    }

    override ngOnInit(): void {
        if (!this.userService.isApplicant()) {
            // Well, they're neither fish nor fowl, so
            // let's clear out what is in storage and
            // require a sign-in
            this.userService.logout();
            this.router.navigateByUrl("/sign-in");

            // Most important - exit
            return;
        }

        this.requiresTask = false;
        super.ngOnInit();

        sessionStorage.removeItem('ro-form-redirect');

        // Just in case no-one else has done this yet
        this.subscription = this.tracker.trackerEventStream$
            .subscribe(event => {
                // console.info(`Got event from subscription: ${JSON.stringify(event)}`);
                // if (event.event == TrackerEventType.TasksLoaded || event.event == TrackerEventType.ActiveTaskChanged) {
                if (event.event == TrackerEventType.TaskStatusUpdateCompleted) {
                    console.log(`A task status change was picked up!`);
                    this.updateTasks();
                }
            });
        var applicantId = this.userService.applicantId();
        this.getReturnerReadyToSubmit(applicantId);
        this.updateTasks();

        this.userService.userPush
            .pushAlert
            .subscribe({
                next: (data: any) => {
                    //console.debug(`Got a push alert from the user push service!`, data);

                    if (this.showAlert(data.title, data.text)) {
                        this.router.navigateByUrl(data.redirect);
                    }
                }
            });
    }

    override ngOnDestroy(): void {
        if (this.subscription)
            this.subscription.unsubscribe();
    }

    updateTasks() {
        // const userId = this.userService.userId();
        // const applicantId = this.userService.applicantId();

        this.isLoading = true;

        if (this.userService.isApplicant() && (!this.userService.hasLatestTermsAndConditions())) {
            // console.log(`USER NEEDS NEW TERMS AND CONDITIONS`);
            this.router.navigateByUrl("/user/terms-and-conditions/latest/ANY");
            return;
        }

        // // console.log(`calling reload user data`);
        // this.userService.reloadUserData().subscribe(
        //     r => {
        //         if (this.userService.isApplicant() && (!this.userService.hasLatestTermsAndConditions())) {
        //             // console.log(`USER NEEDS NEW TERMS AND CONDITIONS`);
        //             this.router.navigateByUrl("/user/terms-and-conditions/latest/ANY");
        //             return;
        //         }
        //     });

        // console.log(`calling loadTasksForApplicant`);
        this.tracker
            .loadTasksForApplicant()
            .subscribe({
                next: (taskList: Task[]) => {
                    // Quick check
                    if (this.tracker.overrideTask) {
                        this.router.navigateByUrl(this.tracker.overrideTask);
                        // Don't bother continuing at this point
                        return;
                    }

                    // Check for todo priority items
                    //console.info(`Checking todo items: ${this.tracker.todoPriority}`);
                    switch (this.tracker.todoPriority) {
                        case ToDoPriorityItem.MedicalDisclosure:
                            this.priorityTask = "DISC_MED";
                            break;
                        case ToDoPriorityItem.CriminalDisclosure:
                            this.priorityTask = "DISC_CRIM";
                            break;
                    }

                    if (this.tracker.chainTasks) {
                        this.findNextTask();
                    } else {
                        // console.log(`Got tasks:`, taskList);
                        let passedCompleteIndex = 999;
                        let completeCount = 0;
                        let participant = this.tracker.isParticipant;
                        let placed = this.userService.userState() === 'ParticipantPlaced';
                        this.initialiseTaskDetails();

                        this.tasks.forEach((t, idx) => {
                            const tasks = this.tracker.tasksForGroup(t.groupId);
                            if (tasks) {
                                if (t.name == 'Submit Application') {
                                    t.class = "submit";
                                }
                                else {
                                    // default: not yet available
                                    let status: number = this.getTaskStatus(t, tasks);
                                    if (status == TaskDetailStatus.Waiting && idx < passedCompleteIndex) {
                                        // Current
                                        passedCompleteIndex = idx;
                                        status = TaskDetailStatus.Current;
                                    }
                                    t.status = status;
                                    switch (status) {
                                        case TaskDetailStatus.Waiting:
                                            t.class = "waiting";
                                            break;
                                        case TaskDetailStatus.Current:
                                            t.class = "current";
                                            break;
                                        case TaskDetailStatus.Complete:
                                            completeCount++;
                                            if ((t.lockable && participant) || (t.plcLockable && placed)) {
                                                t.class = "locked";
                                            } else {
                                                t.class = "complete";
                                            }
                                            break;
                                    }
                                }
                            }
                        });
                        // console.log(`Task count: ${this.tasks.length}, complete: ${completeCount}`);
                        this.allComplete = this.tasks.length === completeCount;
                    }
                    this.isLoading = false;
                },
                error: (error: any) => {
                    this.isLoading = false;
                    this.showError('Application', `An error happened while loading your task list!`, error);
                }
            });
    }

    /**
     * @description Find the task status from consensus between all tasks in group
     * @param detail (TaskDetail)
     * @param tasksInGroup (Task[])
     */
    getTaskStatus(detail: TaskDetail, tasksInGroup: Task[]): TaskDetailStatus {
        let tds = TaskDetailStatus.Waiting;
        let countOfTasks = tasksInGroup.length;
        let completeCount = 0;

        tasksInGroup.forEach(tig => {
            const uts = this.tracker.getUserTaskStatusById(tig.id);
            if (uts && uts.complete) {
                completeCount++;
            }
        });

        //let completed = tasksInGroup.filter(g => g.complete);
        //completeCount = completed.length;

        //console.log(`getTaskStatus: count: ${countOfTasks}, completeCount ${completeCount}`)

        if (completeCount == countOfTasks) {
            tds = TaskDetailStatus.Complete;
        }

        return tds;
    }

    /**
     * @description We can assume that the tracker service actually
     * has loaded all tasks and rules for this applicant by now.
     * We should ask it for what the user must do next
     */
    findNextTask() {
        const action = this.tracker.findNextStep();
        if (action.task) {
            // console.log(`found action task!`, action)
            this.tracker.startTask(action.task);
        } else {
            if (action.urlTarget && action.urlTarget !== "/home") {
                // console.log(`Home::findNextTask() redirecting to ${action.urlTarget}`);

                // If there is no task associated with this action,
                // navigate directly there, otherwise, we have a 
                // task that needs to be actioned
                this.router.navigateByUrl(action.urlTarget);
                // } else {
                //     console.debug(`No active task`);
            }
        }
    }

    clickTask(task: TaskDetail) {
        // console.log(`Task clicked: ${JSON.stringify(task)}`);
        if (task.status == TaskDetailStatus.Waiting) {
            return;
        }

        if (task.name == 'Submit Application') {
            this.router.navigateByUrl("/payments/returner");
            return;
        }

        const tasksForGroup = this.tracker.tasksForGroup(task.groupId);
        let target: Task | undefined = undefined;
        let url: string | undefined = undefined;

        if (task.status == TaskDetailStatus.Complete) {
            // console.info(`Task is complete`);
            // Find the last task and choose between the 
            // standard or review url if there is one
            const reviewable = tasksForGroup.filter((t: Task) => t.routerUrlReview!.length > 0);
            if (reviewable && reviewable.length > 0) {
                target = reviewable[0];
                url = target.routerUrlReview || target.routerUrl;
            }
        }

        if (task.status == TaskDetailStatus.Current) {
            // tasksForGroup.forEach((tig, idx) => { console.info(`Task ${idx} = ${tig.name} ? = ${tig.complete}`) });

            // One or more of these should be incomplete
            const accessible = tasksForGroup.filter(t => t.complete == false);
            if (accessible && accessible.length > 0) {
                target = accessible[0];
                url = target.routerUrl;
            }
        }

        if (!target) {
            //console.info(`No task with a review status - grab the first`, tasksForGroup);
            target = tasksForGroup[0];
            url = target.routerUrl;
        }

        // console.assert(target, "Task is null!");
        // console.info(`Navigate to ${url} for ${target.name}`);

        // For returns
        // this.tracker.setReturnUrl( "/hub");
        // console.log(`Got task: ${JSON.stringify(task)}, routerUrl: +${url}+`);
        if (task.status == TaskDetailStatus.Complete && url) {
            sessionStorage.setItem('ro-form-redirect', '/hub');
            this.tracker.setActiveTask(target);
            this.router.navigateByUrl(url);
        } else {
            // We want to load any intro page if there is one at this point
            target.taskStatus = TaskStatus.Initialising;
            this.tracker.startTask(target);
        }
    }

    goToUploads() {
        this.router.navigateByUrl("/uploads");
    }

    goToSummary() {
        this.router.navigateByUrl("/application-summary");
    }

    initialiseTaskDetails() {
        if (this.tracker.returnerTasks) {
            this.tasks.push(new TaskDetail(2, 2, 'Previous Participation', "", true, true));
            this.tasks.push(new TaskDetail(8, 8, 'Availability', "Now we'd like you to tell us when you are available to take part in the Camp America programme.", true, true));
            this.tasks.push(new TaskDetail(11, 11, 'Education', "Education, education, education. We need to know about where you've studied or are studying."));
            this.tasks.push(new TaskDetail(13, 12, 'Work History', "If you've any work experience, it can make placing you even easier!"));
            this.tasks.push(new TaskDetail(15, 14, 'Contact Details', "Let us know your contact address, and someone we can talk to in case of emergency."));
            this.tasks.push(new TaskDetail(16, 15, 'Medical Questions', "", true, true));
            this.tasks.push(new TaskDetail(22, 19, 'Criminal Questions', "", true, true));
            this.tasks.push(new TaskDetail(17, 16, 'Flight Choice', "Now we're going to look at your departure preferences!", true, true));
            this.tasks.push(new TaskDetail(19, 18, 'Passport Details', ""));
        } else {
            this.tasks.push(new TaskDetail(7, 7, 'Skills', "To help your interviewer, we need to know a little bit more about your skills.", false, true));
            this.tasks.push(new TaskDetail(8, 8, 'Availability', "Now we'd like you to tell us when you are available to take part in the Camp America programme.", false, true));
            this.tasks.push(new TaskDetail(9, 9, 'About Me', "A little bit more information about you can make placing you at camp much easier!"));
            this.tasks.push(new TaskDetail(10, 10, 'Camp Options', "We recruit for hundreds of different camps – let us know which camp types you are happy to be considered for!", false, true));
            this.tasks.push(new TaskDetail(11, 11, 'Education', "Education, education, education. We need to know about where you've studied or are studying."));
            this.tasks.push(new TaskDetail(13, 12, 'Work History', "If you've any work experience, it can make placing you even easier!"));
            this.tasks.push(new TaskDetail(15, 14, 'Contact Details', "Let us know your contact address, and someone we can talk to in case of emergency."));
            this.tasks.push(new TaskDetail(16, 15, 'Medical Questions', "Medical questions boilerplate!", true, true));
            this.tasks.push(new TaskDetail(17, 16, 'Flight Choice', "Now we're going to look at your departure preferences!", true, true));
            this.tasks.push(new TaskDetail(18, 17, 'Tell Us More', "Just a few more things left to do before we can start showing your application to camps..."));
            this.tasks.push(new TaskDetail(19, 18, 'Passport Details', ""));
            this.tasks.push(new TaskDetail(14, 13, 'Referees', "Let us know some people who can tell us why you’d be amazing on a summer camp next summer!"));
        }

        if (this.showSubmitApplicationButton) {
            this.tasks.push(new TaskDetail(0, 0, 'Submit Application', "Review and Submit Application"));
        }
    }

    getReturnerReadyToSubmit(applicantId: number) {
        this.tracker
            .getReturnerSubmittedApplication(applicantId)
            .subscribe({
                next: (readyToSubmit: boolean) => {
                    this.showSubmitApplicationButton = readyToSubmit;
                },
                error: (error: any) => this.pageError(<any>error)
            });
    }

    showAlert(title: string, explain: string, error: string | undefined = undefined): boolean {
        this.modalService
            .showModalInformation(title, explain)
            .subscribe({
                next: (r: string) => {

                }
            });

        return false;
    }

    showError(title: string, explain: string, error: string | undefined = undefined): boolean {
        this.modalService
            .showModalError(title, explain)
            .subscribe({
                next: (r: string) => {

                }
            });

        return false;
    }
    title: string = "My Application";
    isLoading = false;
    taskList?: Task[];
    tasks: TaskDetail[] = [];
    priorityTask?: string;
    statusText: string = "This is the area for status text. If there is status text assigned to a task which is current, we will put something useful in.";
    showSubmitApplicationButton = false;
    subscription?: Subscription;
    allComplete = false;
}


